/**
 * 🎼 musicTheory.ts — Extensión de guitarEngine + chordEngine
 *
 * Funciones de teoría musical de alto nivel para:
 * - Transposición de acordes por nombre
 * - Escalas recomendadas por acorde
 * - Conversión de números romanos a progresiones reales
 */

import {
  type NoteName,
  type SharpName,
  noteToSemitones,
  semitonesToNote,
  shouldUseFlats,
} from "./guitarEngine";
import {
  type ExtendedChordQuality,
  CHORD_CATALOG,
  getChordDef,
} from "./chordEngine";

// ============================================
// TIPOS
// ============================================

export interface ParsedChord {
  root: NoteName;
  quality: ExtendedChordQuality;
  bass?: NoteName; // para inversiones slash (C/E)
  rawQuality: string; // parte del string después de la raíz
}

export interface ScaleResult {
  notes: NoteName[];
  scaleName: string;
  mode: string;
}

// ============================================
// MAPAS DE CALIDAD (string → ExtendedChordQuality)
// ============================================

const QUALITY_ALIASES: Record<string, ExtendedChordQuality> = {
  "": "major",
  "maj": "major",
  "M": "major",
  "m": "minor",
  "min": "minor",
  "-": "minor",
  "dim": "dim",
  "°": "dim",
  "aug": "aug",
  "+": "aug",
  "sus2": "sus2",
  "sus4": "sus4",
  "sus": "sus4",
  "maj7": "maj7",
  "M7": "maj7",
  "Δ7": "maj7",
  "7": "7",
  "m7": "m7",
  "min7": "m7",
  "-7": "m7",
  "m7b5": "m7b5",
  "ø7": "m7b5",
  "dim7": "dim7",
  "°7": "dim7",
  "aug7": "aug7",
  "+7": "aug7",
  "mMaj7": "mMaj7",
  "mM7": "mMaj7",
  "-M7": "mMaj7",
  "7sus4": "7sus4",
  "7sus": "7sus4",
  "7sus2": "7sus2",
  "7b5": "7b5",
  "7#5": "7sharp5",
  "7sharp5": "7sharp5",
  "9": "9",
  "m9": "m9",
  "min9": "m9",
  "-9": "m9",
  "maj9": "maj9",
  "M9": "maj9",
  "11": "11",
  "m11": "m11",
  "min11": "m11",
  "-11": "m11",
  "maj11": "maj11",
  "M11": "maj11",
  "13": "13",
  "m13": "m13",
  "min13": "m13",
  "-13": "m13",
  "maj13": "maj13",
  "M13": "maj13",
  "add2": "add2",
  "add4": "add4",
  "add9": "add9",
  "add11": "add11",
  "6": "6",
  "m6": "m6",
  "min6": "m6",
  "-6": "m6",
  "7b9": "7b9",
  "7#9": "7sharp9",
  "7sharp9": "7sharp9",
  "9b5": "9b5",
  "9#5": "9sharp5",
  "9sharp5": "9sharp5",
  "m9b5": "m9b5",
  "13b9": "13b9",
  "13#9": "13sharp9",
  "13sharp9": "13sharp9",
};

const ROOT_PATTERN = /^([A-G][#b]?)(.*)$/;

// ============================================
// PARSING DE ACORDES
// ============================================

export function parseChordName(name: string): ParsedChord | null {
  const trimmed = name.trim();
  if (!trimmed) return null;

  // Separar inversión slash: C/E → root=C, bass=E
  const slashIdx = trimmed.indexOf("/");
  const beforeSlash = slashIdx >= 0 ? trimmed.slice(0, slashIdx) : trimmed;
  const afterSlash = slashIdx >= 0 ? trimmed.slice(slashIdx + 1) : undefined;

  const match = beforeSlash.match(ROOT_PATTERN);
  if (!match) return null;

  const root = match[1] as NoteName;
  const rawQuality = match[2];

  const quality = QUALITY_ALIASES[rawQuality];
  if (!quality) {
    // Fallback: si no reconocemos la calidad, lo tratamos como mayor
    // Esto permite parsear acordes desconocidos sin romper la app
    if (rawQuality === "") {
      // ok, es mayor
    } else {
      // Intentar heuristic: si empieza con m pero no está en aliases,
      // probablemente sea menor con extensión no soportada → fallback a minor
      if (rawQuality.startsWith("m") && !rawQuality.startsWith("maj")) {
        // pass through as minor (best effort)
      }
    }
  }

  const bass = afterSlash ? (afterSlash.match(ROOT_PATTERN)?.[1] as NoteName | undefined) : undefined;

  return {
    root,
    quality: quality || "major",
    bass,
    rawQuality,
  };
}

// ============================================
// TRANSPOSICIÓN
// ============================================

/**
 * Transpone un acorde escrito en string (ej: "C", "Am7/G") por N semitonos.
 * Respeta sistema de bemoles según el contexto de la tonalidad destino.
 */
export function transposeChord(name: string, semitones: number): string {
  const parsed = parseChordName(name);
  if (!parsed) return name;

  const newRootSemi = (noteToSemitones(parsed.root) + semitones + 12) % 12;
  const useFlats = shouldUseFlats(
    semitonesToNote(newRootSemi, false) as NoteName,
    parsed.quality.includes("minor") || parsed.quality === "dim" ? "minor" : "major"
  );
  const newRoot = semitonesToNote(newRootSemi, useFlats);

  const newBass = parsed.bass
    ? semitonesToNote(
        (noteToSemitones(parsed.bass) + semitones + 12) % 12,
        useFlats
      )
    : undefined;

  const qualityStr = parsed.rawQuality; // conservamos el string original (Am7 sigue siendo m7)
  const slash = newBass ? `/${newBass}` : "";
  return `${newRoot}${qualityStr}${slash}`;
}

/**
 * Transpone un array de acordes.
 */
export function transposeProgression(chords: string[], semitones: number): string[] {
  return chords.map((c) => transposeChord(c, semitones));
}

// ============================================
// ESCALAS POR ACORDE
// ============================================

const MODE_INTERVALS: Record<string, number[]> = {
  ionian: [0, 2, 4, 5, 7, 9, 11],
  dorian: [0, 2, 3, 5, 7, 9, 10],
  phrygian: [0, 1, 3, 5, 7, 8, 10],
  lydian: [0, 2, 4, 6, 7, 9, 11],
  mixolydian: [0, 2, 4, 5, 7, 9, 10],
  aeolian: [0, 2, 3, 5, 7, 8, 10],
  locrian: [0, 1, 3, 5, 6, 8, 10],
  melodic_minor: [0, 2, 3, 5, 7, 9, 11],
  harmonic_minor: [0, 2, 3, 5, 7, 8, 11],
  pentatonic_major: [0, 2, 4, 7, 9],
  pentatonic_minor: [0, 3, 5, 7, 10],
  blues: [0, 3, 5, 6, 7, 10],
};

const CHORD_TO_MODE: Record<string, string> = {
  major: "ionian",
  minor: "aeolian",
  dim: "locrian",
  aug: "lydian",
  sus2: "mixolydian",
  sus4: "mixolydian",
  maj7: "ionian",
  "7": "mixolydian",
  m7: "dorian",
  m7b5: "locrian",
  dim7: "locrian",
  aug7: "lydian",
  mMaj7: "melodic_minor",
  "7sus4": "mixolydian",
  "7sus2": "mixolydian",
  "7b5": "locrian",
  "7sharp5": "lydian",
  "9": "mixolydian",
  m9: "dorian",
  maj9: "ionian",
  "11": "mixolydian",
  m11: "dorian",
  maj11: "ionian",
  "13": "mixolydian",
  m13: "dorian",
  maj13: "ionian",
  add2: "ionian",
  add4: "ionian",
  add9: "ionian",
  add11: "ionian",
  "6": "ionian",
  m6: "melodic_minor",
  "7b9": "phrygian_dominant", // especial
  "7sharp9": "altered", // especial
};

export function getScaleForChord(
  chordName: string,
  modeOverride?: string
): ScaleResult {
  const parsed = parseChordName(chordName);
  if (!parsed) {
    return { notes: [], scaleName: "Desconocido", mode: "unknown" };
  }

  const def = getChordDef(parsed.quality);
  const modeKey = modeOverride || CHORD_TO_MODE[parsed.quality] || "ionian";
  const intervals = MODE_INTERVALS[modeKey] || MODE_INTERVALS["ionian"];

  const rootSemi = noteToSemitones(parsed.root);
  const useFlats = shouldUseFlats(parsed.root, modeKey.includes("minor") || parsed.quality.includes("m") ? "minor" : "major");

  const notes = intervals.map((interval) =>
    semitonesToNote((rootSemi + interval) % 12, useFlats)
  );

  const scaleName = `${parsed.root} ${modeKey.replace(/_/g, " ")}`;
  return { notes, scaleName, mode: modeKey };
}

/**
 * Devuelve las notas del acorde separadas por función (raíz, tercera, quinta, tensiones).
 * Útil para colorear el diapasón.
 */
export function getChordTones(chordName: string): {
  root: NoteName;
  third?: NoteName;
  fifth?: NoteName;
  tensions: NoteName[];
} | null {
  const parsed = parseChordName(chordName);
  if (!parsed) return null;

  const def = getChordDef(parsed.quality);
  if (!def) {
    // Fallback para acordes no catalogados (ej mayor)
    const rootSemi = noteToSemitones(parsed.root);
    const useFlats = shouldUseFlats(parsed.root, "major");
    return {
      root: parsed.root,
      third: semitonesToNote((rootSemi + 4) % 12, useFlats),
      fifth: semitonesToNote((rootSemi + 7) % 12, useFlats),
      tensions: [],
    };
  }

  const rootSemi = noteToSemitones(parsed.root);
  const useFlats = shouldUseFlats(parsed.root, def.quality.includes("m") && !def.quality.includes("maj") ? "minor" : "major");

  const tones = def.intervals.map((interval) =>
    semitonesToNote((rootSemi + interval) % 12, useFlats)
  );

  return {
    root: tones[0],
    third: tones[1],
    fifth: tones[2],
    tensions: tones.slice(3),
  };
}

// ============================================
// NÚMEROS ROMANOS → ACORDES
// ============================================

const ROMAN_MAP: Record<string, number> = {
  I: 0, II: 2, III: 4, IV: 5, V: 7, VI: 9, VII: 11,
};

const ROMAN_MINOR_INDICATORS = new Set([
  "ii", "iii", "vi", "vii",
  "ii°", "vii°",
  "ii7", "iii7", "vi7", "vii7",
  "iiø7", "viiø7",
]);

function romanToSemitone(roman: string): number {
  const upper = roman.toUpperCase().replace(/[^IV]+/g, "");
  return ROMAN_MAP[upper] ?? 0;
}

function romanQuality(roman: string): ExtendedChordQuality {
  const lower = roman.toLowerCase();
  const hasDim = lower.includes("°") || lower.includes("dim") || lower.includes("ø");
  const hasAug = lower.includes("+") || lower.includes("aug");
  const has7 = lower.includes("7");
  const hasMaj7 = lower.includes("maj7") || lower.includes("Δ7");
  const has9 = lower.includes("9");
  const hasSus = lower.includes("sus");

  // Determinar si es menor por el casing del número romano base (sin símbolos)
  const romanBase = roman.replace(/[^iv]+/gi, "");
  const isMinor = romanBase.length > 0 && romanBase === romanBase.toLowerCase();

  if (hasSus && has7) return "7sus4";
  if (hasSus) return "sus4";
  if (hasDim && has7) return "m7b5"; // ø7
  if (hasDim) return "dim";
  if (hasAug && has7) return "aug7";
  if (hasAug) return "aug";
  if (hasMaj7) return "maj7";
  if (has9) return isMinor ? "m9" : "9";
  if (has7) return isMinor ? "m7" : "7";

  // Sin número: triada
  return isMinor ? "minor" : "major";
}

/**
 * Convierte una progresión de números romanos a nombres de acordes reales.
 * Ej: romanNumeralsToChords("C", ["ii", "V", "I"]) → ["Dm7", "G7", "Cmaj7"]
 *
 * Opciones:
 * - `seventh`: fuerza a usar tétradas por defecto (ii7, V7, Imaj7)
 * - `extend`: fuerza tensiones (ii9, V13, Imaj9)
 */
export function romanNumeralsToChords(
  key: NoteName,
  progression: string[],
  options: { seventh?: boolean; extend?: boolean } = {}
): string[] {
  const keySemi = noteToSemitones(key);
  const useFlats = shouldUseFlats(key, "major");

  return progression.map((roman) => {
    const semi = (keySemi + romanToSemitone(roman)) % 12;
    const root = semitonesToNote(semi, useFlats);

    let quality = romanQuality(roman);

    // Overrides por opciones
    if (options.extend) {
      if (quality === "minor") quality = "m9";
      else if (quality === "major") quality = "maj9";
      else if (quality === "7") quality = "13";
      else if (quality === "m7") quality = "m9";
    } else if (options.seventh) {
      // Mapeo funcional: I/IV → maj7, V → 7 dominante, ii/iii/vi → m7, vii° → m7b5
      const romanBase = roman.replace(/[^iv]+/gi, "").toUpperCase();
      const degreeMap: Record<string, ExtendedChordQuality> = {
        I: "maj7", II: "m7", III: "m7", IV: "maj7",
        V: "7", VI: "m7", VII: "m7b5",
      };
      const forced = degreeMap[romanBase];
      if (forced) {
        quality = forced;
      } else {
        if (quality === "minor") quality = "m7";
        else if (quality === "major") quality = "maj7";
        else if (quality === "dim") quality = "m7b5";
      }
    }

    // Construir string de calidad legible
    const qualityStr = getQualityDisplayString(quality);
    return `${root}${qualityStr}`;
  });
}

export function getQualityDisplayString(quality: ExtendedChordQuality): string {
  const displayMap: Record<string, string> = {
    major: "",
    minor: "m",
    dim: "dim",
    aug: "aug",
    sus2: "sus2",
    sus4: "sus4",
    maj7: "maj7",
    "7": "7",
    m7: "m7",
    m7b5: "m7b5",
    dim7: "dim7",
    aug7: "aug7",
    mMaj7: "mMaj7",
    "7sus4": "7sus4",
    "7sus2": "7sus2",
    "7b5": "7b5",
    "7sharp5": "7#5",
    "9": "9",
    m9: "m9",
    maj9: "maj9",
    "11": "11",
    m11: "m11",
    maj11: "maj11",
    "13": "13",
    m13: "m13",
    maj13: "maj13",
    add2: "add2",
    add4: "add4",
    add9: "add9",
    add11: "add11",
    "6": "6",
    m6: "m6",
    "7b9": "7b9",
    "7sharp9": "7#9",
    "9b5": "9b5",
    "9sharp5": "9#5",
    m9b5: "m9b5",
    "13b9": "13b9",
    "13sharp9": "13#9",
  };
  return displayMap[quality] || quality;
}

// ============================================
// CAPO / CEJILLA
// ============================================

/**
 * Aplica cejilla virtual: devuelve los acordes "como se ven" con capo en traste N.
 * Ej: capoChord("C", 3) → "A" (porque con capo 3, el A se toca como C)
 *
 * Útil para mostrar al usuario: "Suena en C, pero tócalo con forma de A capo 3".
 */
export function capoChord(shapedChord: string, capoFret: number): string {
  // El acorde escrito ya es la forma del capo. Para saber qué suena realmente,
  // transponemos hacia arriba por los semitonos del capo.
  return transposeChord(shapedChord, capoFret);
}

/**
 * Inverso: dado un acorde objetivo y un capo, devuelve la forma que debe usar.
 * Ej: shapeForCapo("C", 3) → "A" (toca forma de A con capo 3 para que suene C)
 */
export function shapeForCapo(targetChord: string, capoFret: number): string {
  return transposeChord(targetChord, -capoFret);
}

// ============================================
// UTILIDADES PARA UI
// ============================================

/**
 * Devuelve un objeto con los grados funcionales de una progresión.
 * Útil para resaltar colores en el diapasón.
 */
export function getChordFunctionColor(degreeName: string): string {
  if (degreeName === "R" || degreeName === "1") return "indigo";
  if (degreeName.includes("3")) return "emerald";
  if (degreeName.includes("5")) return "slate";
  if (degreeName.includes("7") || degreeName.includes("9") || degreeName.includes("11") || degreeName.includes("13")) return "amber";
  if (degreeName.includes("2") || degreeName.includes("4") || degreeName.includes("6")) return "sky";
  return "zinc";
}

/**
 * Convierte un grado (1-7) a nombre de acorde en una tonalidad.
 * Ej: degreeToChord("C", 2, "minor") → "Dm"
 */
export function degreeToChord(
  key: NoteName,
  degree: number,
  qualityHint?: ExtendedChordQuality
): string {
  const keySemi = noteToSemitones(key);
  const scale = MODE_INTERVALS["ionian"];
  const targetSemi = (keySemi + scale[(degree - 1) % 7]) % 12;
  const useFlats = shouldUseFlats(key, "major");
  const root = semitonesToNote(targetSemi, useFlats);

  const defaultQualities: ExtendedChordQuality[] = [
    "major", "minor", "minor", "major", "major", "minor", "m7b5",
  ];
  const quality = qualityHint || defaultQualities[(degree - 1) % 7];
  return `${root}${getQualityDisplayString(quality)}`;
}

/**
 * Convierte un acorde real a su número romano relativo a una tonalidad.
 * Ej: chordToRomanNumeral("Dm7", "C") → "ii"
 *     chordToRomanNumeral("G7", "C") → "V"
 *     chordToRomanNumeral("Cmaj7", "C") → "I"
 */
/**
 * Devuelve las notas de un acorde como pares {note, octave} en root position,
 * listas para tocar con el sintetizador (playNote).
 * Octavas asignadas por rango MIDI para sonar bien en conjunto.
 */
export function getChordMidiNotes(chordName: string): { note: NoteName; octave: number }[] {
  const parsed = parseChordName(chordName);
  if (!parsed) return [];

  const def = getChordDef(parsed.quality);
  const rootSemi = noteToSemitones(parsed.root);
  const useFlats = shouldUseFlats(parsed.root, def?.quality.includes("m") && !def.quality.includes("maj") ? "minor" : "major");

  const intervals = def?.intervals ?? [0, 4, 7]; // fallback major triad

  // Asignar octavas para que el acorde suene balanceado (root ~ C4)
  const baseOctave = 4;
  return intervals.map((interval) => {
    const semi = (rootSemi + interval) % 12;
    const note = semitonesToNote(semi, useFlats);
    // Si el intervalo es mayor que una octava (tensiones 9, 11, 13), subir octava
    const octaveShift = interval >= 12 ? 1 : 0;
    return { note, octave: baseOctave + octaveShift };
  });
}

export function chordToRomanNumeral(chordName: string, key: NoteName): string {
  const parsed = parseChordName(chordName);
  if (!parsed) return "?";

  const keySemi = noteToSemitones(key);
  const rootSemi = noteToSemitones(parsed.root);
  const interval = ((rootSemi - keySemi) % 12 + 12) % 12;

  const romanMap: Record<number, { upper: string; lower: string }> = {
    0: { upper: "I", lower: "i" },
    2: { upper: "II", lower: "ii" },
    4: { upper: "III", lower: "iii" },
    5: { upper: "IV", lower: "iv" },
    7: { upper: "V", lower: "v" },
    9: { upper: "VI", lower: "vi" },
    11: { upper: "VII", lower: "vii" },
  };

  const entry = romanMap[interval];
  if (!entry) return "?";

  const isMinor =
    parsed.quality === "minor" ||
    parsed.quality === "m7" ||
    parsed.quality === "m9" ||
    parsed.quality === "m11" ||
    parsed.quality === "m13" ||
    parsed.quality === "mMaj7" ||
    parsed.quality === "dim" ||
    parsed.quality === "dim7" ||
    parsed.quality === "m7b5";

  const base = isMinor ? entry.lower : entry.upper;

  // Añadir calidad extendida al romano
  if (parsed.quality === "maj7" || parsed.quality === "maj9" || parsed.quality === "maj11" || parsed.quality === "maj13") return base + "maj7";
  if (parsed.quality === "7" || parsed.quality === "9" || parsed.quality === "11" || parsed.quality === "13") return base + "7";
  if (parsed.quality === "dim" || parsed.quality === "dim7") return base + "°";
  if (parsed.quality === "m7b5") return base + "ø7";
  if (parsed.quality === "aug" || parsed.quality === "aug7") return base + "+";
  if (parsed.quality === "sus2" || parsed.quality === "sus4" || parsed.quality === "7sus4") return base + "sus";

  return base;
}
